import { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, ChevronDown as ExpandIcon, Eye, EyeOff, Trash2, PlusCircle } from 'lucide-react'
import { AdminGalleryModal } from './gallery-modal'
import { useAdminCatalog } from '@/hooks/use-admin-catalog'
import type { PageComponent, BlockConfig } from '@/types/admin'

// ─── BlockRow ─────────────────────────────────────────────────────────────────

function BlockRow({
  block,
  index,
  total,
  onChange,
  onMove,
  onDelete,
  catalogMap,
}: {
  block: BlockConfig
  index: number
  total: number
  onChange: (b: BlockConfig) => void
  onMove: (from: number, to: number) => void
  onDelete: (index: number) => void
  catalogMap: Record<string, PageComponent>
}) {
  const [expanded, setExpanded] = useState(false)
  const def = catalogMap[block.component]
  const label = def?.label ?? block.component
  const category = def?.category ?? ''
  const fields = def?.settings_schema ?? []

  function setField(key: string, value: unknown) {
    onChange({ ...block, settings: { ...block.settings, [key]: value } })
  }

  return (
    <div className={`rounded-xl border bg-card overflow-hidden ${!block.enabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-2 px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, index + 1)}
            disabled={index === total - 1}
            className="rounded p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium">{label}</span>
          {category && (
            <span className="ml-2 text-[10px] uppercase tracking-wide text-muted-foreground">
              {category}
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={() => onChange({ ...block, enabled: !block.enabled })}
          className="text-muted-foreground hover:text-foreground"
          title={block.enabled ? 'Sembunyikan' : 'Tampilkan'}
        >
          {block.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>

        <button
          type="button"
          onClick={() => onDelete(index)}
          className="text-muted-foreground hover:text-destructive"
          title="Hapus block"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>

        {fields.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ExpandIcon className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {expanded && fields.length > 0 && (
        <div className="border-t border-border px-4 py-4 bg-background/50 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Pengaturan</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {fields.map((field) => (
              <div key={field.key} className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">{field.label}</label>

                {field.type === 'boolean' ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={(block.settings[field.key] as boolean) ?? false}
                      onChange={(e) => setField(field.key, e.target.checked)}
                      className="h-4 w-4 rounded border-input accent-primary"
                    />
                    <span className="text-xs text-muted-foreground">
                      {(block.settings[field.key] as boolean) ? 'Ya' : 'Tidak'}
                    </span>
                  </div>
                ) : field.type === 'select' ? (
                  <select
                    value={(block.settings[field.key] as string) ?? ''}
                    onChange={(e) => setField(field.key, e.target.value || undefined)}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    <option value="">Default</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={(block.settings[field.key] as string) ?? ''}
                    onChange={(e) => setField(field.key, e.target.value || undefined)}
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring resize-y"
                    placeholder={field.placeholder}
                  />
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={(block.settings[field.key] as string | number) ?? ''}
                    onChange={(e) =>
                      setField(
                        field.key,
                        field.type === 'number'
                          ? (e.target.value ? Number(e.target.value) : undefined)
                          : (e.target.value || undefined)
                      )
                    }
                    className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder={field.placeholder ?? 'Default'}
                    step={field.type === 'number' ? '0.1' : undefined}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── AdminBlockEditor ─────────────────────────────────────────────────────────

interface AdminBlockEditorProps {
  /** Current blocks — controlled by parent (no internal save button) */
  blocks: BlockConfig[]
  onChange: (blocks: BlockConfig[]) => void
}

/**
 * Controlled block editor for use inside admin theme preset forms.
 * Does NOT have its own Save button — the parent form handles saving.
 */
export function AdminBlockEditor({ blocks, onChange }: AdminBlockEditorProps) {
  const { data: catalogComponents = [], isLoading: catalogLoading } = useAdminCatalog()
  const [galleryOpen, setGalleryOpen] = useState(false)

  const catalogMap = useMemo(
    () => Object.fromEntries(catalogComponents.map((c) => [c.component_key, c])),
    [catalogComponents]
  )

  function addBlock(def: PageComponent) {
    const newBlock: BlockConfig = {
      id: crypto.randomUUID(),
      component: def.component_key,
      enabled: true,
      order: blocks.length + 1,
      settings: { ...def.default_settings },
    }
    onChange([...blocks, newBlock])
  }

  function updateBlock(index: number, updated: BlockConfig) {
    onChange(blocks.map((b, i) => (i === index ? updated : b)))
  }

  function deleteBlock(index: number) {
    onChange(blocks.filter((_, i) => i !== index).map((b, i) => ({ ...b, order: i + 1 })))
  }

  function moveBlock(from: number, to: number) {
    if (to < 0 || to >= blocks.length) return
    const next = [...blocks]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    onChange(next.map((b, i) => ({ ...b, order: i + 1 })))
  }

  if (catalogLoading) {
    return <p className="text-sm text-muted-foreground">Memuat komponen...</p>
  }

  return (
    <>
      <div className="space-y-3">
        {blocks.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8 border border-dashed border-border rounded-xl">
            Belum ada block. Tambahkan dari galeri komponen.
          </p>
        )}
        {blocks.map((block, index) => (
          <BlockRow
            key={block.id}
            block={block}
            index={index}
            total={blocks.length}
            onChange={(updated) => updateBlock(index, updated)}
            onMove={moveBlock}
            onDelete={deleteBlock}
            catalogMap={catalogMap}
          />
        ))}

        <button
          type="button"
          onClick={() => setGalleryOpen(true)}
          className="flex items-center gap-2 rounded-md border border-dashed border-border px-4 py-2 text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors w-full justify-center"
        >
          <PlusCircle className="h-4 w-4" />
          Tambah Komponen
        </button>
      </div>

      <AdminGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={addBlock}
        components={catalogComponents}
        isLoading={catalogLoading}
      />
    </>
  )
}
